import uniqBy from 'lodash/uniqBy';
import uniq from 'lodash/uniq';
import keyBy from 'lodash/keyBy';
import { State } from '../types';
import { CartLinkedEntitlementsSchema, CartProductSchema, OperationType, ProductStatus } from '../../types';
import { isEntitlementChanged } from './is-entitlement-changed';
import { isNewEntitlement } from './is-new-entitlement';
import { getIdsOfLinkedProductsAndApps } from './get-linked-products-and-apps';
import { truthy } from '../../utils';
import { CONTAINER_PRODUCT_ID, getAppIdentifier, OmsPayload } from './utils';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { JIRA_RELATIONSHIP_OFFERING_KEY } from '@css-xp/ppc-constants';

const fetchLinkedProductsAndAppsLineItems = ({
  initialStoreProducts,
  cartApps,
  selectedProducts,
  invoiceGroupId,
  identifier,
}: {
  initialStoreProducts: State['products'];
  invoiceGroupId?: string;
  selectedProducts: State['selectedProducts'];
  cartApps: State['linkedEntitlements'];
  identifier: string;
}) => {
  const linkedProducts: CartProductSchema[] = [];
  const linkedApps: CartLinkedEntitlementsSchema[] = [];
  const includedProduct = initialStoreProducts[identifier];
  // finding existing linked products not added in the cart or 'selectedProducts' list
  const { productIds: linkedProductIds } = getIdsOfLinkedProductsAndApps({
    productId: includedProduct.id,
    productsObj: initialStoreProducts,
    appsObj: cartApps as Record<string, CartLinkedEntitlementsSchema[]>,
  });
  // checking entitlements which are not added in the cart and  not changed
  // but linked to an entitlement with IG change
  const linkedNonChangedExistingProductIds = linkedProductIds.filter((id) => {
    return (
      !isEntitlementChanged(selectedProducts[id]) &&
      !isNewEntitlement(initialStoreProducts[id]) &&
      initialStoreProducts[id].sourceInvoiceGroupId !== invoiceGroupId
    );
  });
  linkedNonChangedExistingProductIds.forEach((id) => {
    linkedProducts.push(initialStoreProducts[id]);
  });
  // finding existing linked apps added in the cart but not changed
  const appsIdentifier = getAppIdentifier(includedProduct);
  const appsNotChanged =
    cartApps?.[appsIdentifier]?.filter((app) => {
      return !isNewEntitlement(app) && app.sourceInvoiceGroupId !== invoiceGroupId;
    }) ?? [];

  linkedApps.push(...appsNotChanged);

  return {
    linkedProducts,
    linkedApps,
  };
};

/**
 * for amendment + billing profile change, we create additional payload items like
 * e.g. cloud_site_1
 => IG1
 -> JSW - container1 => Amended + Changed to IG2
 -> JSM - container1 (linked product)
 -> JWM - container1 (linked product)
 -> APP - container1 (linked product)

 additional payload items should look like
 JSW => ACCOUNT_MODIFICATION_ORDER
 JSM => ACCOUNT_MODIFICATION_ORDER
 JWM => ACCOUNT_MODIFICATION_ORDER
 APP => ACCOUNT_MODIFICATION_ORDER
 *
 * Here, we are generating additional payload items for billing profile change.
 */
// 1. JSM existing, JSW existing => Add app + JWM => Edit JSM => Change billing profile
// 2. JSM existing => Add app + JWM => Change billing profile
// 3. Existing Product is editing and BP is changed
// 4. Free entitlement
export function getLineItemsForDestinationBillingProfileChange({
  initialStoreProducts,
  invoiceGroupId,
  selectedProducts,
  cartApps,
  isIGChangeForFreeEntitlementEnabled,
}: {
  initialStoreProducts?: State['products'];
  invoiceGroupId?: string;
  selectedProducts: State['selectedProducts'];
  cartApps: State['linkedEntitlements'];
  isIGChangeForFreeEntitlementEnabled?: boolean;
}): OmsPayload['entitlements'] {
  const payloadItems: OmsPayload['entitlements'] = [];
  const linkedProductsForLineItem: CartProductSchema[] = [];
  const linkedProductsApps: CartLinkedEntitlementsSchema[] = [];

  for (const identifier in selectedProducts) {
    if (!Object.prototype.hasOwnProperty.call(selectedProducts, identifier)) {
      continue;
    }
    // checking modified products and that product should not be having transfer case
    if (
      initialStoreProducts?.[identifier].operationType !== OperationType.TRANSFER &&
      selectedProducts[identifier].productStatuses?.includes(ProductStatus.EDITED)
    ) {
      const product = initialStoreProducts?.[identifier];

      /**
       * Here, checking destination billing profile and source billing profile
       * If both IG's are different then we are trying to place an order for account modification
       **/
      if (invoiceGroupId !== product?.sourceInvoiceGroupId) {
        const containerId = product?.containerId;

        const allProducts = Object.values(initialStoreProducts ?? {});
        const linkedProducts = allProducts?.filter((product) => {
          // filtering linked products based on container id or product id
          // we need to send current product, identifier
          // for siblings we need to only send those which are existing
          return (
            ((containerId && product.containerId === containerId) || product.id === identifier) &&
            !isNewEntitlement(product)
          );
        });
        if (product) {
          // getting linked apps based on container id or product id
          const linkedApps = cartApps?.[containerId || product?.id] ?? [];
          linkedProductsApps.push(
            ...linkedApps.filter((app) => !isNewEntitlement(app) && app.sourceInvoiceGroupId !== invoiceGroupId)
          );
        }

        linkedProductsForLineItem.push(...linkedProducts);
      }
    }

    if (
      !isEntitlementChanged(selectedProducts[identifier]) &&
      initialStoreProducts?.[identifier].operationType !== OperationType.TRANSFER &&
      initialStoreProducts?.[identifier] &&
      !isNewEntitlement(initialStoreProducts[identifier]) &&
      initialStoreProducts?.[identifier].sourceInvoiceGroupId !== invoiceGroupId
    ) {
      const nonChangedExistingProduct = initialStoreProducts[identifier];
      linkedProductsForLineItem.push(nonChangedExistingProduct);

      const { linkedProducts, linkedApps } = fetchLinkedProductsAndAppsLineItems({
        initialStoreProducts,
        invoiceGroupId,
        cartApps,
        selectedProducts,
        identifier,
      });
      linkedProductsForLineItem.push(...linkedProducts);
      linkedProductsApps.push(...linkedApps);
    }
    // adding the entitlements which are not adding to the cart but linked to new purchased entitlement
    // e.g. JSM + Apps added on BP2 while JSW exists on BP1
    if (
      initialStoreProducts &&
      identifier &&
      initialStoreProducts[identifier] &&
      isNewEntitlement(initialStoreProducts[identifier])
    ) {
      const { linkedProducts, linkedApps } = fetchLinkedProductsAndAppsLineItems({
        initialStoreProducts,
        invoiceGroupId,
        cartApps,
        selectedProducts,
        identifier,
      });
      linkedProductsForLineItem.push(...linkedProducts);
      linkedProductsApps.push(...linkedApps);
    }
  }
  const allLinkedProducts = uniqBy<CartProductSchema>(linkedProductsForLineItem, 'id');
  const allLinkedApps = uniqBy<CartLinkedEntitlementsSchema>(linkedProductsApps, 'id');

  const containerIds = uniq(allLinkedProducts?.map(({ containerId }) => containerId)?.filter(truthy) ?? []);

  // Here, we are generating account modification payload for container products
  const accountModificationItemsForContainer =
    containerIds?.map((id) => {
      const productDetails = allLinkedProducts?.find(({ containerId }) => containerId === id);

      /**
       * Here, generating account modification payload for container products
       **/
      return {
        entitlementId: id as string, // container id
        orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
        productId: CONTAINER_PRODUCT_ID,
        offeringId: JIRA_RELATIONSHIP_OFFERING_KEY,
        metadata: {
          sourceTransactionAccountId: productDetails?.sourceTransactionAccountId ?? '',
          sourceInvoiceGroupId: productDetails?.sourceInvoiceGroupId ?? '',
          // pass site id as metadata attribute to OMS engine
          siteId: productDetails?.cloudSiteId ?? '',
        },
      };
    }) ?? [];

  const accountModificationItemsForProducts = allLinkedProducts?.map((product) => {
    const productDetails = product;
    const entitlementId = product.id;
    const initialOfferingId = initialStoreProducts?.[entitlementId]?.offeringId ?? '';

    /**
     * Here, generating account modification payload for linked products
     **/
    return {
      entitlementId,
      orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
      productId: product.productId,
      offeringId: initialOfferingId,
      metadata: {
        sourceTransactionAccountId: productDetails.sourceTransactionAccountId ?? '',
        sourceInvoiceGroupId: productDetails.sourceInvoiceGroupId ?? '',
        // pass site id as metadata attribute to OMS engine
        siteId: productDetails.cloudSiteId,
      },
    };
  });

  const idOrContainerIdToProductMap = keyBy(allLinkedProducts, ({ id, containerId }) => containerId || id);
  const accountModificationItemsForApps = allLinkedApps.map((app) => {
    const entitlementId = app.id;
    const productOrContainerId = app.containerOrProductId;
    const parentProduct = idOrContainerIdToProductMap[productOrContainerId];
    /**
     * Here, generating account modification payload for linked apps
     **/
    return {
      entitlementId,
      orderType: OrderItemType.ACCOUNT_MODIFICATION_ORDER,
      productId: app.productId,
      offeringId: app.offeringId,
      metadata: {
        sourceTransactionAccountId: parentProduct.sourceTransactionAccountId ?? '',
        sourceInvoiceGroupId: parentProduct.sourceInvoiceGroupId ?? app.sourceInvoiceGroupId ?? '',
        // pass site id as metadata attribute to OMS engine
        siteId: parentProduct.cloudSiteId,
      },
    };
  });

  return payloadItems.concat(
    accountModificationItemsForProducts,
    accountModificationItemsForApps,
    ...(isIGChangeForFreeEntitlementEnabled ? [...accountModificationItemsForContainer] : [])
  );
}
